import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, History, Loader2, RefreshCw, X } from 'lucide-react';
import {
  fetchVendorProfileUpdateById,
  fetchVendorProfileUpdates,
  type VendorProfileUpdateLogRow,
} from '../../services/MasterAdmin/vendorProfileUpdateLogService';

const UPDATED_BY_LABELS: Record<string, string> = {
  VENDOR: 'Vendor',
  MASTER_ADMIN: 'Master Admin',
  SYSTEM: 'System',
};

const formatUpdatedBy = (value?: string | null) =>
  value ? UPDATED_BY_LABELS[value] || value : '—';

const detailLabelClass = 'text-sm text-gray-500 dark:text-gray-400';
const detailValueClass = 'font-medium text-gray-900 dark:text-gray-100';

const JsonBlock: React.FC<{ title: string; data: unknown }> = ({ title, data }) => (
  <div>
    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {title}
    </h4>
    <pre className="max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100">
      {JSON.stringify(data, null, 2)}
    </pre>
  </div>
);

const DetailDrawer: React.FC<{
  row: VendorProfileUpdateLogRow | null;
  loading: boolean;
  onClose: () => void;
}> = ({ row, loading, onClose }) => {
  if (!row && !loading) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Profile update details
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : row ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <span className={detailLabelClass}>Type</span>
                  <p className={detailValueClass}>{row.profileTypeLabel || row.profileType}</p>
                </div>
                <div>
                  <span className={detailLabelClass}>Updated at</span>
                  <p className={detailValueClass}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </p>
                </div>
                <div>
                  <span className={detailLabelClass}>Updated by</span>
                  <p className={detailValueClass}>{formatUpdatedBy(row.updatedByType)}</p>
                </div>
                <div>
                  <span className={detailLabelClass}>Endpoint</span>
                  <p className="break-all font-mono text-xs text-gray-800 dark:text-gray-200">
                    {row.sourceEndpoint || '—'}
                  </p>
                </div>
              </div>
              <div>
                <span className={detailLabelClass}>Changed fields</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(row.changedFields || []).map((f) => (
                    <span
                      key={f}
                      className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/40 dark:text-primary-200"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              {row.updatePayload && (
                <JsonBlock title="Update payload (request)" data={row.updatePayload} />
              )}
              {row.profileBefore && (
                <JsonBlock title="Profile before" data={row.profileBefore} />
              )}
              <JsonBlock title="Profile after" data={row.profileAfter} />
              {row.metadata && <JsonBlock title="Metadata" data={row.metadata} />}
            </div>
          ) : null}
        </div>
      </aside>
    </div>,
    document.body
  );
};

const PAGE_SIZE = 25;

type VendorProfileUpdateHistoryPanelProps = {
  vendorId: string;
  vendorName?: string;
};

/** Profile update audit trail filtered by vendorId — for vendor dashboards. */
const VendorProfileUpdateHistoryPanel: React.FC<VendorProfileUpdateHistoryPanelProps> = ({
  vendorId,
  vendorName,
}) => {
  const [rows, setRows] = useState<VendorProfileUpdateLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VendorProfileUpdateLogRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fetchingMoreRef = useRef(false);

  const loadPage = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (!vendorId) return;
      if (pageToLoad === 1) {
        setLoading(true);
      } else {
        if (fetchingMoreRef.current) return;
        fetchingMoreRef.current = true;
        setLoadingMore(true);
      }
      setError(null);
      try {
        const res = await fetchVendorProfileUpdates({
          page: pageToLoad,
          limit: PAGE_SIZE,
          vendorId,
        });
        const nextRows = res.data || [];
        const tp = res.pagination?.totalPages ?? 1;
        setTotalPages(tp);
        setHasMore(pageToLoad < tp);
        setPage(pageToLoad);
        setRows((prev) => (append ? [...prev, ...nextRows] : nextRows));
      } catch {
        setError('Failed to load profile update history.');
        if (!append) setRows([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        fetchingMoreRef.current = false;
      }
    },
    [vendorId]
  );

  useEffect(() => {
    setPage(1);
    setRows([]);
    void loadPage(1, false);
  }, [loadPage]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          void loadPage(page + 1, true);
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, loadPage]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetchVendorProfileUpdateById(id);
      setDetail(res.data);
    } catch {
      setError('Failed to load update details.');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700 sm:px-5">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            <History className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Profile update history
          </h3>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {vendorName
              ? `Audit trail for ${vendorName}`
              : 'Changes this vendor made to their onboarding profile.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadPage(1, false)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 sm:mx-5">
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          No profile updates recorded for this vendor yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">When</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Changed</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">By</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    {row.profileTypeLabel || row.profileType}
                  </td>
                  <td className="max-w-md px-4 py-3">
                    <p className="line-clamp-2 text-gray-700 dark:text-gray-300">
                      {(row.changedFields || []).join(', ') || '—'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                    {formatUpdatedBy(row.updatedByType)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void openDetail(row.id)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      View
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(loadingMore || (hasMore && rows.length > 0)) && (
        <div
          ref={loadMoreRef}
          className="flex items-center justify-center gap-2 border-t border-gray-100 py-4 dark:border-gray-700"
        >
          {loadingMore ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading more…</span>
            </>
          ) : hasMore ? (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Scroll for more (page {page} of {totalPages})
            </span>
          ) : null}
        </div>
      )}

      {selectedId && (
        <DetailDrawer
          row={detail}
          loading={detailLoading}
          onClose={() => {
            setSelectedId(null);
            setDetail(null);
          }}
        />
      )}
    </div>
  );
};

export default VendorProfileUpdateHistoryPanel;
